import { Injectable } from '@angular/core';

/**
 * In-memory translation cache for synchronous O(1) lookups.
 * Stores translations as Map<moduleName, Map<key, translationObj>>
 * so the TranslatePipe can read without JSON.parse or linear .find() calls.
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationCacheService {

  private cache = new Map<string, Map<string, any>>();

  /**
   * Index a module's translation entries into the in-memory cache.
   * Each entry is expected to have a `key` property.
   */
  hydrate(moduleName: string, entries: any[]): void {
    if (!entries || !Array.isArray(entries)) return;

    const moduleMap = new Map<string, any>();
    for (const entry of entries) {
      if (entry?.key) {
        moduleMap.set(entry.key, entry);
      }
    }
    this.cache.set(moduleName, moduleMap);
  }

  /**
   * Synchronous O(1) lookup for a translation key within a module.
   */
  lookup(moduleName: string, key: string): any | null {
    return this.cache.get(moduleName)?.get(key) ?? null;
  }

  /**
   * Lookup across multiple modules (for merged modules like Onboarding+Surveys).
   * Returns the first match found in order.
   */
  lookupMultiple(moduleNames: string[], key: string): any | null {
    for (const mod of moduleNames) {
      const result = this.lookup(mod, key);
      if (result) return result;
    }
    return null;
  }

  /**
   * Whether the cache has been populated with at least one module.
   */
  isHydrated(): boolean {
    return this.cache.size > 0;
  }

  /**
   * Clear all cached translations.
   */
  clear(): void {
    this.cache.clear();
  }
}
